import React, { useState, useEffect } from 'react';
import {
	useGetMaintainersQuery,
	useCreateMaintainerMutation,
	useUpdateMaintainerMutation,
	useDeleteMaintainerMutation,
	Maintainer,
	useListAllUnitsQuery,
	useGetMaintainerUnitsQuery,
	useUpdateMaintainerAssignmentsMutation,
	MaintainerAssignment,
} from '../store/apiSlice';
import { useNotification } from '../contexts/NotificationContext';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import './Maintainers.css';

const Maintainers: React.FC = () => {
	const { showSuccess, showError } = useNotification();
	const { data: maintainersRes, isLoading: initialLoading } = useGetMaintainersQuery();
	const [createMaintainer, { isLoading: creating }] = useCreateMaintainerMutation();
	const [updateMaintainer, { isLoading: updating }] = useUpdateMaintainerMutation();
	const [deleteMaintainer, { isLoading: deleting }] = useDeleteMaintainerMutation();

	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingMaintainer, setEditingMaintainer] = useState<Maintainer | null>(null);
	const [selectedMaintainer, setSelectedMaintainer] = useState<Maintainer | null>(null);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [maintainerToDelete, setMaintainerToDelete] = useState<Maintainer | null>(null);

	const [formData, setFormData] = useState({
		cognitoUsername: '',
		email: '',
		displayName: '',
		password: '',
	});

	const maintainers = maintainersRes?.data || [];
	const actionLoading = creating || updating || deleting;

	const handleCreate = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!formData.cognitoUsername.trim() || !formData.email.trim()) return;

		try {
			await createMaintainer({
				cognitoUsername: formData.cognitoUsername.trim(),
				email: formData.email.trim(),
				displayName: formData.displayName.trim() || undefined,
				password: formData.password.trim() || undefined,
			}).unwrap();

			resetForm();
			setShowCreateForm(false);
			showSuccess('Maintainer created successfully');
		} catch (error: any) {
			console.error('Error creating maintainer:', error);
			const errorMessage = error.data?.message || error.message || 'Failed to create maintainer';
			showError(errorMessage);
		}
	};

	const handleEdit = (maintainer: Maintainer) => {
		setEditingMaintainer(maintainer);
		setFormData({
			cognitoUsername: maintainer.cognitoUsername,
			email: maintainer.email,
			displayName: maintainer.displayName || '',
			password: '',
		});
		setShowCreateForm(true);
	};

	const handleUpdate = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!editingMaintainer || !formData.email.trim()) return;

		try {
			await updateMaintainer({
				username: editingMaintainer.cognitoUsername,
				email: formData.email.trim(),
				displayName: formData.displayName.trim() || undefined,
			}).unwrap();

			resetForm();
			setEditingMaintainer(null);
			setShowCreateForm(false);
			showSuccess('Maintainer updated successfully');
		} catch (error: any) {
			console.error('Error updating maintainer:', error);
			const errorMessage = error.data?.message || error.message || 'Failed to update maintainer';
			showError(errorMessage);
		}
	};

	const handleDeleteClick = (maintainer: Maintainer) => {
		setMaintainerToDelete(maintainer);
		setShowDeleteDialog(true);
	};

	const handleDelete = async () => {
		if (!maintainerToDelete) return;

		setShowDeleteDialog(false);
		try {
			await deleteMaintainer(maintainerToDelete.cognitoUsername).unwrap();
			showSuccess('Maintainer deleted successfully');
			setMaintainerToDelete(null);
		} catch (error: any) {
			console.error('Error deleting maintainer:', error);
			const errorMessage = error.data?.message || error.message || 'Failed to delete maintainer';
			showError(errorMessage);
			setMaintainerToDelete(null);
		}
	};

	const resetForm = () => {
		setFormData({
			cognitoUsername: '',
			email: '',
			displayName: '',
			password: '',
		});
		setEditingMaintainer(null);
	};

	const handleCancel = () => {
		resetForm();
		setShowCreateForm(false);
	};

	return (
		<Layout>
			<div className="maintainers-page">
				<header className="maintainers-header">
					<h1>Maintainer Users</h1>
					<button
						onClick={() => {
							resetForm();
							setShowCreateForm(true);
						}}
						className="create-button"
						disabled={actionLoading}
					>
						+ Create Maintainer
					</button>
				</header>

				{showCreateForm && (
					<div className="create-form">
						<h2>{editingMaintainer ? 'Edit Maintainer' : 'Create Maintainer'}</h2>
						<form onSubmit={editingMaintainer ? handleUpdate : handleCreate}>
							<div className="form-group">
								<label htmlFor="username">Cognito Username *</label>
								<input
									id="username"
									type="text"
									placeholder="e.g., maintainer1@example.com"
									value={formData.cognitoUsername}
									onChange={(e) => setFormData({ ...formData, cognitoUsername: e.target.value })}
									required
									disabled={!!editingMaintainer || actionLoading}
								/>
							</div>

							<div className="form-group">
								<label htmlFor="email">Email *</label>
								<input
									id="email"
									type="email"
									placeholder="e.g., maintainer1@example.com"
									value={formData.email}
									onChange={(e) => setFormData({ ...formData, email: e.target.value })}
									required
									disabled={actionLoading}
								/>
							</div>

							<div className="form-group">
								<label htmlFor="displayName">Display Name</label>
								<input
									id="displayName"
									type="text"
									placeholder="e.g., John Doe"
									value={formData.displayName}
									onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
									disabled={actionLoading}
								/>
							</div>

							{!editingMaintainer && (
								<div className="form-group">
									<label htmlFor="password">Password (optional - for new Cognito users)</label>
									<input
										id="password"
										type="password"
										placeholder="Temporary Password"
										value={formData.password}
										onChange={(e) => setFormData({ ...formData, password: e.target.value })}
										disabled={actionLoading}
									/>
								</div>
							)}

							<div className="form-actions">
								<button type="submit" disabled={actionLoading}>
									{actionLoading ? 'Saving...' : editingMaintainer ? 'Update' : 'Create'}
								</button>
								<button type="button" onClick={handleCancel} disabled={actionLoading}>
									Cancel
								</button>
							</div>
						</form>
					</div>
				)}

				<div className="maintainers-list">
					{initialLoading && maintainers.length === 0 ? (
						<div className="loading-indicator">Loading maintainers...</div>
					) : maintainers.length === 0 ? (
						<p>No maintainers created yet. Add your first maintainer user!</p>
					) : (
						<table className="maintainers-table">
							<thead>
								<tr>
									<th>Username / ID</th>
									<th>Display Name</th>
									<th>Email</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{maintainers.map((m) => (
									<tr key={m.cognitoUsername}>
										<td>
											<strong>{m.cognitoUsername}</strong>
										</td>
										<td>{m.displayName || '-'}</td>
										<td>{m.email}</td>
										<td>
											<div className="action-buttons">
												<button
													onClick={() => {
														setSelectedMaintainer(m);
														setTimeout(() => {
															document.getElementById('assignments-panel')?.scrollIntoView({ behavior: 'smooth' });
														}, 100);
													}}
													className="assign-button"
													disabled={actionLoading}
													style={{
														padding: '6px 12px',
														border: 'none',
														borderRadius: '4px',
														cursor: 'pointer',
														fontSize: '13px',
														fontWeight: '500',
														backgroundColor: '#20c997',
														color: 'white',
													}}
												>
													Assignments
												</button>
												<button
													onClick={() => handleEdit(m)}
													className="edit-button"
													disabled={actionLoading}
												>
													Edit
												</button>
												<button
													onClick={() => handleDeleteClick(m)}
													className="delete-button"
													disabled={actionLoading}
												>
													Delete
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>

				<ConfirmDialog
					isOpen={showDeleteDialog}
					title="Delete Maintainer"
					message={`Are you sure you want to delete maintainer "${maintainerToDelete?.cognitoUsername}"? This will remove all their unit maintainer assignments.`}
					confirmText="Delete"
					cancelText="Cancel"
					type="danger"
					onConfirm={handleDelete}
					onCancel={() => {
						setShowDeleteDialog(false);
						setMaintainerToDelete(null);
					}}
				/>

				{selectedMaintainer && (
					<div id="assignments-panel" className="assignments-section" style={{ marginTop: '40px' }}>
						<UnitAssignmentsPanel
							maintainer={selectedMaintainer}
							onClose={() => setSelectedMaintainer(null)}
						/>
					</div>
				)}
			</div>
		</Layout>
	);
};

interface UnitAssignmentsPanelProps {
	maintainer: Maintainer;
	onClose: () => void;
}

const UnitAssignmentsPanel: React.FC<UnitAssignmentsPanelProps> = ({ maintainer, onClose }) => {
	const { showSuccess, showError } = useNotification();
	const { data: unitsRes, isLoading: loadingUnits } = useListAllUnitsQuery();
	const { data: assignmentsRes, isLoading: loadingAssignments } = useGetMaintainerUnitsQuery(maintainer.cognitoUsername);
	const [updateAssignments, { isLoading: saving }] = useUpdateMaintainerAssignmentsMutation();

	const allUnits = unitsRes?.data || [];

	const [selectedUnits, setSelectedUnits] = useState<Record<string, string>>({});
	const [filterText, setFilterText] = useState('');

	useEffect(() => {
		if (assignmentsRes?.data) {
			const initialMap: Record<string, string> = {};
			assignmentsRes.data.forEach((a) => {
				initialMap[a.unitId] = a.permissionLevel;
			});
			setSelectedUnits(initialMap);
		}
	}, [assignmentsRes, maintainer.cognitoUsername]);

	const getTenantName = (tenantId: string) => tenantId.replace('Tenant#', '');

	const getSolutionName = (solutionId: string) => {
		const parts = solutionId.split('#Solution#');
		return parts.length === 2 ? parts[1] : solutionId;
	};

	const filteredUnits = allUnits.filter((unit) => {
		const text = filterText.toLowerCase();
		const unitName = (unit.name || '').toLowerCase();
		const tenantName = getTenantName(unit.tenantId).toLowerCase();
		const solutionName = getSolutionName(unit.solutionId).toLowerCase();
		return unitName.includes(text) || tenantName.includes(text) || solutionName.includes(text);
	});

	const handleToggleUnit = (unitId: string) => {
		setSelectedUnits((prev) => {
			const copy = { ...prev };
			if (copy[unitId] !== undefined) {
				delete copy[unitId];
			} else {
				copy[unitId] = 'Novice';
			}
			return copy;
		});
	};

	const handlePermissionChange = (unitId: string, level: string) => {
		setSelectedUnits((prev) => ({
			...prev,
			[unitId]: level,
		}));
	};

	const handleSelectAll = () => {
		const newMap = { ...selectedUnits };
		filteredUnits.forEach((u) => {
			if (newMap[u.unitId] === undefined) {
				newMap[u.unitId] = 'Novice';
			}
		});
		setSelectedUnits(newMap);
	};

	const handleDeselectAll = () => {
		const newMap = { ...selectedUnits };
		filteredUnits.forEach((u) => {
			delete newMap[u.unitId];
		});
		setSelectedUnits(newMap);
	};

	const handleSave = async () => {
		try {
			const assignmentsPayload: MaintainerAssignment[] = Object.entries(selectedUnits).map(([unitId, permissionLevel]) => {
				const unit = allUnits.find((u) => u.unitId === unitId);
				return {
					tenantId: unit?.tenantId || '',
					solutionId: unit?.solutionId || '',
					unitId,
					permissionLevel,
				};
			});

			await updateAssignments({
				username: maintainer.cognitoUsername,
				assignments: assignmentsPayload,
			}).unwrap();

			showSuccess('Assignments updated successfully');
		} catch (err: any) {
			console.error('Error saving assignments:', err);
			showError(err.data?.message || err.message || 'Failed to save assignments');
		}
	};

	const isLoading = loadingUnits || loadingAssignments;

	if (isLoading) {
		return <div className="loading-indicator">Loading assignments details...</div>;
	}

	return (
		<div className="assignments-panel">
			<div className="assignments-panel-header">
				<h2>Manage Unit Assignments for {maintainer.displayName || maintainer.cognitoUsername}</h2>
				<button onClick={onClose} className="close-panel-btn">✕</button>
			</div>

			<div className="assignments-filter-bar">
				<input
					type="text"
					placeholder="Filter units by name, tenant, or solution..."
					value={filterText}
					onChange={(e) => setFilterText(e.target.value)}
					className="filter-input"
				/>
				<div className="bulk-actions">
					<button onClick={handleSelectAll} className="bulk-btn select-all-btn">Select All</button>
					<button onClick={handleDeselectAll} className="bulk-btn deselect-all-btn">Deselect All</button>
				</div>
			</div>

			<div className="assignments-table-container">
				<table className="assignments-table">
					<thead>
						<tr>
							<th style={{ width: '40px', textAlign: 'center' }}>Select</th>
							<th>Unit Name</th>
							<th>Tenant</th>
							<th>Solution</th>
							<th>Permission Level</th>
						</tr>
					</thead>
					<tbody>
						{filteredUnits.length === 0 ? (
							<tr>
								<td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
									No units found.
								</td>
							</tr>
						) : (
							filteredUnits.map((unit) => {
								const isChecked = selectedUnits[unit.unitId] !== undefined;
								return (
									<tr key={unit.unitId} className={isChecked ? 'row-selected' : ''}>
										<td style={{ textAlign: 'center' }}>
											<input
												type="checkbox"
												checked={isChecked}
												onChange={() => handleToggleUnit(unit.unitId)}
												style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
											/>
										</td>
										<td>
											<strong>{unit.name}</strong>
										</td>
										<td>{getTenantName(unit.tenantId)}</td>
										<td>{getSolutionName(unit.solutionId)}</td>
										<td>
											<select
												value={selectedUnits[unit.unitId] || 'Novice'}
												onChange={(e) => handlePermissionChange(unit.unitId, e.target.value)}
												disabled={!isChecked}
												className="permission-select"
											>
												<option value="Novice">Novice</option>
												<option value="Intermediate">Intermediate</option>
												<option value="Advanced">Advanced</option>
												<option value="Expert">Expert</option>
												<option value="Master">Master</option>
											</select>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			<div className="assignments-footer">
				<button onClick={handleSave} disabled={saving} className="save-assignments-btn">
					{saving ? 'Saving...' : 'Save Assignments'}
				</button>
				<button onClick={onClose} disabled={saving} className="cancel-assignments-btn">
					Cancel
				</button>
			</div>
		</div>
	);
};

export default Maintainers;
